import { Column } from "primereact/column";
import { studentsApi } from "../../services/students/studentsApi";
import { Skeleton } from "primereact/skeleton";
import { DataTable } from "../../components/data/DataTable";
import { Link, useLocation } from "react-router-dom";
import { StudentResponse } from "../../models/dtos";
import { Button } from "primereact/button";
import Error from "../../components/common/Error";
import { useSelector } from "react-redux";
import {
  getStudentsFilters,
  serializeFilters,
  initializeFromUrl,
  setSelectedAcademicYears,
} from "../../services/students/studentsSlice";
import { Toolbar } from "primereact/toolbar";
import { useEffect, useRef, useState } from "react";
import { useAppDispatch } from "../../hooks/redux";
import { setContextSidebar } from "../../services/base/layoutSlice";
import { SplitButton } from "primereact/splitbutton";
import useFileDownload from "../../hooks/useFileDownload";
import { facultyNumberTemplate } from "../../components/common/FacultyNumberTemplate";
import { FilterService } from "primereact/api";
import MassAttestationDialog from "./MassAttestationDialog";
import MassEnrollmentDialog from "./MassEnrollmentDialog";
import { Tag } from "primereact/tag";

FilterService.register("custom_uan", (value, filterValue) => {
  if (filterValue === true) {
    if (typeof value === "string") {
      return value.trim() === "";
    }
    return value === null || value === undefined || value === "";
  } else if (filterValue === false) {
    if (typeof value === "string") {
      return value.trim() !== "";
    }
    return value !== null && value !== undefined;
  } else if (typeof filterValue === "string") {
    return value?.toString().toLowerCase().includes(filterValue.toLowerCase());
  }
  return true;
});

const StudentsList = () => {
  const studentsFilters = useSelector(getStudentsFilters);
  const dispatch = useAppDispatch();
  const location = useLocation();
  const dt = useRef<any>(null);
  const [showMassAttestationDialog, setShowMassAttestationDialog] = useState(false);
  const [showMassEnrollmentDialog, setShowMassEnrollmentDialog] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [lazyParams, setLazyParams] = useState<any>({
    first: 0,
    rows: 50,
    page: 0,
    sortField: null,
    sortOrder: null,
    filters: {
      facultyNumber: { value: null, matchMode: "contains" },
      firstName: { value: null, matchMode: "contains" },
      middleName: { value: null, matchMode: "contains" },
      lastName: { value: null, matchMode: "contains" },
      egn: { value: null, matchMode: "contains" },
      uan: { value: null, matchMode: "custom" },
      specialityName: { value: null, matchMode: "contains" },
      semester: { value: null, matchMode: "contains" },
      studentStatusName: { value: null, matchMode: "contains" },
      enrollmentDate: { value: null, matchMode: "contains" },
    },
  });

  useEffect(() => {
    const queryString = location.search.substring(1);
    if (queryString) {
      dispatch(initializeFromUrl(queryString));
    } else {
      dispatch(
        setSelectedAcademicYears([
          {
            academicYearId: 46,
            name: "2026/2027",
            yearNumber: 2026,
          },
        ]),
      );
    }
    setIsInitialized(true);
  }, []);

  useEffect(() => {
    if (!isInitialized) return;

    const queryString = serializeFilters(studentsFilters);
    const newUrl = queryString ? `${location.pathname}?${queryString}` : location.pathname;
    window.history.replaceState(null, "", newUrl);
  }, [studentsFilters, location.pathname, isInitialized]);

  const filter = {
    byFaculty: studentsFilters.faculties.map((x) => ({
      id: x.facultyId,
    })),
    bySpeciality: studentsFilters.specialities.map((x) => ({
      id: x.specialityId,
    })),
    byDegree: studentsFilters.degrees.map((x) => ({
      id: x.degreeId,
    })),
    byEducType: studentsFilters.eduForms.map((x) => ({
      id: x.specialityModeId,
    })),
    byFeeType: studentsFilters.fees.map((x) => ({
      id: x.feeTypeId,
    })),
    byCourse: studentsFilters.courseYears.map((x) => ({
      course: x.course,
    })),
    byAcademicYear: studentsFilters.academicYears.map((x) => ({
      id: x.academicYearId,
    })),
    byStudentsStatus: studentsFilters.studentStatuses.map((x) => ({
      id: x.studentStatusId,
    })),
    byEnrollmentDate: studentsFilters.enrollmentDates,
    byIsForeigner: studentsFilters.isForeigner,
    byIsStudyingInEnglish: studentsFilters.isStudyingInEnglish,
    byIsBulgarianCitizen: studentsFilters.isBulgarianCitizen,
  };

  const {
    data: studentsPage,
    error,
    isLoading,
    isFetching,
    refetch,
  } = studentsApi.useGetStudentsQuery({ ...filter, ...lazyParams }, { skip: !isInitialized });

  const [triggerExport] = studentsApi.useLazyExportQuery();
  const { downloadFile } = useFileDownload(triggerExport);
  const [triggerExportMoodle] = studentsApi.useLazyExportForMoodleQuery();
  const { downloadFile: downloadMoodleFile } = useFileDownload(triggerExportMoodle);

  const [multiSendToNacid] = studentsApi.useMultiSendDataToNacidMutation();
  const [multiUpdateEmail] = studentsApi.useMultiUpdateEmailMutation();

  useEffect(() => {
    dispatch(setContextSidebar(true));

    return () => {
      dispatch(setContextSidebar(false));
    };
  }, [dispatch]);

  const handleExport = () => {
    const { first, rows, page, ...exportParams } = lazyParams;
    downloadFile({ ...filter, ...exportParams });
  };

  const handleExportMoodle = () => {
    const { first, rows, page, ...exportParams } = lazyParams;
    downloadMoodleFile({ ...filter, ...exportParams });
  };

  const handleSendToNacid = () => {
    const selected = dt.current?.getSelectedRows();
    if (selected?.length) {
      multiSendToNacid(selected.map((x: StudentResponse) => x.studentId));
    }
  };

  const handleUpdateEmail = () => {
    const selected = dt.current?.getSelectedRows();
    if (selected?.length) {
      multiUpdateEmail(selected.map((x: StudentResponse) => x.studentId));
    }
  };

  const handleRefresh = () => {
    refetch();
  };

  const handleMassAttestation = () => {
    const selected = dt.current?.getSelectedRows();
    if (selected?.length) {
      setShowMassAttestationDialog(true);
    }
  };

  const handleMassEnrollment = () => {
    const selected = dt.current?.getSelectedRows();
    if (selected?.length) {
      setShowMassEnrollmentDialog(true);
    }
  };

  const topButtons = (
    <div className="flex flex-row gap-2">
      <Link to="/students/enrollment">
        <Button label="Записване на студент от КСК" icon="pi pi-plus" severity="success" />
      </Link>
      <Link to="/students/create">
        <Button label="Ръчно записване на студент" icon="pi pi-plus" severity="secondary" />
      </Link>
      <Button label="Обновяване" icon="pi pi-refresh" severity="info" onClick={handleRefresh} />
    </div>
  );

  const extraButtons = (
    <div className="flex flex-row gap-2">
      <SplitButton
        label="Действия"
        icon="pi pi-cog"
        model={[
          {
            label: "Изпращане към НАЦИД",
            icon: "pi pi-send",
            command: handleSendToNacid,
          },
          {
            label: "Промяна на група",
            icon: "pi pi-envelope",
            command: () => {
              // Logic for sending email to selected students
              console.log("Sending email to selected students");
            },
          },
          {
            label: "Промяна на учебен план",
            icon: "pi pi-pencil",
            command: () => {
              // Logic for sending email to selected students
              console.log("Sending email to selected students");
            },
          },
          {
            label: "Промяна на статус",
            icon: "pi pi-user-plus",
            command: () => {
              // Logic for sending email to selected students
              console.log("Sending email to selected students");
            },
          },
          {
            label: "Обновяване на и-мейл",
            icon: "pi pi-send",
            command: handleUpdateEmail,
          },
          {
            label: "Масова заверка",
            icon: "pi pi-check-circle",
            command: handleMassAttestation,
          },
          {
            label: "Записване за следващ семестър",
            icon: "pi pi-arrow-right",
            command: handleMassEnrollment,
          },
        ]}
      />
      <SplitButton
        label="Експорт"
        icon="pi pi-upload"
        severity="secondary"
        model={[
          {
            label: "Основни данни",
            icon: "pi pi-file",
            command: handleExport,
          },
          {
            label: "За Moodle",
            icon: "pi pi-file",
            command: handleExportMoodle,
          },
        ]}
      />
    </div>
  );

  if (isLoading) return <Skeleton className="w-full h-2rem" />;
  if (error) return <Error error={error} />;

  return (
    <div className="sticky top-0">
      <h1 className="mb-4 mt-0 text-4xl font-light">Студенти</h1>
      <Toolbar start={topButtons} end={extraButtons} />
      <DataTable
        ref={dt}
        value={(studentsPage?.data as any)?.data ?? []}
        emptyMessage="Не бяха открити студенти."
        lazy
        dataKey="studentId"
        paginator
        rows={lazyParams.rows}
        rowsPerPageOptions={[10, 25, 50, 100]}
        totalRecords={(studentsPage?.data as any)?.totalRecords ?? 0}
        loading={isFetching}
        onPage={(e: any) => setLazyParams((prev: any) => ({ ...prev, first: e.first, rows: e.rows, page: e.page }))}
        onSort={(e: any) => setLazyParams((prev: any) => ({ ...prev, sortField: e.sortField, sortOrder: e.sortOrder }))}
        onFilter={(e: any) => setLazyParams((prev: any) => ({ ...prev, first: 0, filters: e.filters }))}
        sortField={lazyParams.sortField}
        sortOrder={lazyParams.sortOrder}
        first={lazyParams.first}
        filters={lazyParams.filters}
        filterDisplay="row"
        // stateStorage="session"
        // stateKey="dt-students-list"
      >
        <Column field="facultyNumber" header="Факултетен номер" body={facultyNumberTemplate} sortable filter></Column>
        <Column field="firstName" header="Име" sortable filter></Column>
        <Column field="middleName" header="Презиме" sortable filter></Column>
        <Column field="lastName" header="Фамилия" sortable filter></Column>
        <Column field="egn" header="ЕГН" sortable filter></Column>
        <Column
          field="uan"
          header="ЕАН"
          sortable
          filter
          showFilterMenu={false}
          filterElement={(options) => (
            <div className="flex gap-2">
              <input
                type="text"
                value={typeof options.value === "string" ? options.value : ""}
                onChange={(e) => options.filterApplyCallback(e.target.value)}
                className="p-inputtext p-component"
              />
              <Button
                icon="pi pi-stop-circle"
                size="small"
                severity="secondary"
                onClick={() => {
                  const currentValue = options.value;
                  const newValue = currentValue === "true" ? "false" : "true";
                  options.filterApplyCallback(newValue);
                }}
                tooltip="Філтър празни/непразни"
              />
            </div>
          )}
          filterMatchMode="custom"
        ></Column>
        <Column field="facultyName" header="Факултет" hidden></Column>
        <Column field="specialityName" header="Специалност" sortable filter></Column>
        <Column field="specialityModeName" header="Форма на обучение" hidden></Column>
        <Column field="specialityTypeName" header="Такса" hidden></Column>
        <Column field="groupName" header="Група" hidden></Column>
        <Column field="semester" header="Семестър" sortable filter></Column>
        <Column
          field="studentStatusName"
          header="Статус"
          sortable
          filter
          body={(row) => (
            <Tag
              value={row.studentStatusName}
              severity={
                row.studentStatusName === "приключил"
                  ? "danger"
                  : row.studentStatusName === "прекъснал"
                    ? "warning"
                    : "info"
              }
            />
          )}
        ></Column>
        <Column
          field="lastSemesterAttestation"
          header="Заверен семестър"
          body={(row) =>
            row.lastSemesterAttestation ? (
              <span className="text-xs">
                {row.lastSemesterAttestation?.semester} / {row.lastSemesterAttestation?.semesterAttestationTypeName}
              </span>
            ) : (
              <span className="text-xs">-</span>
            )
          }
        ></Column>
        <Column
          header="Записан"
          sortable
          filter
          filterField="enrollmentDate"
          showFilterMenu={false}
          body={(row) => (
            <span className="text-xs">
              {row.enrolledByUser} / {row.enrollmentDate}
            </span>
          )}
          hidden
        ></Column>
      </DataTable>

      <MassAttestationDialog
        visible={showMassAttestationDialog}
        onHide={() => setShowMassAttestationDialog(false)}
        studentIds={dt.current?.getSelectedRows()?.map((x: StudentResponse) => x.studentId) ?? []}
      />

      <MassEnrollmentDialog
        visible={showMassEnrollmentDialog}
        onHide={() => setShowMassEnrollmentDialog(false)}
        studentIds={dt.current?.getSelectedRows()?.map((x: StudentResponse) => x.studentId) ?? []}
      />
    </div>
  );
};

export default StudentsList;
